'use client'

import { useQuery } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage, Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatDate, getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { LeaderboardType } from './index'
import { useSharedTRPC } from '../../../context'

type HeaderProps = LeaderboardType & {
  className?: string
}

export function Header({ className, ...p }: HeaderProps) {
  const trpc = useSharedTRPC()

  const courseId = p.type === 'course' ? p.courseId : undefined
  const challengeId = p.type === 'challenge' ? p.challengeId : undefined

  const { data, isPending, isError, refetch } = useQuery({
    ...trpc.lesson.getLessonModuleDetails.queryOptions({ challengeId, courseId }),
    staleTime: Infinity,
  })

  const baseClass = cn(
    'srd:w-full srd:bg-neutral-black srd:text-neutral-white srd:pb-6 srd:pt-10 srd:border-b srd:border-outline-variant/50',
    className,
  )

  if (isError) {
    return (
      <section className={baseClass}>
        <div className='srd:w-full srd:max-w-[1440px] srd:mx-auto srd:flex srd:flex-col srd:gap-3'>
          <div className='srd:flex srd:items-center srd:gap-2'>
            <AlertCircle className='srd:size-5 srd:shrink-0' />
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-white')}>
              Gagal memuat detail. Silakan coba lagi.
            </p>
          </div>
          <Button variant='secondary' size='sm' onClick={() => refetch()} className='srd:w-fit'>
            Coba lagi
          </Button>
        </div>
      </section>
    )
  }

  if (isPending) {
    return (
      <section className={baseClass}>
        <div className='srd:w-full srd:max-w-[1440px] srd:mx-auto srd:flex srd:flex-col srd:gap-y-3'>
          <Skeleton className='srd:h-4 srd:w-32 srd:rounded srd:bg-neutral-white/20' />
          <Skeleton className='srd:h-8 srd:w-72 srd:rounded srd:bg-neutral-white/20' />
          <Skeleton className='srd:h-4 srd:w-80 srd:rounded srd:bg-neutral-white/20' />
        </div>
      </section>
    )
  }

  let dateDisplay: string | null = null
  if (data.type === 'challenge') {
    dateDisplay = `${formatDate(data.startDate, { dateStyle: 'long', timeStyle: 'short' })} — ${formatDate(data.endDate, { dateStyle: 'long', timeStyle: 'short' })}`
  } else if (data.type === 'course') {
    const displayStart = data.startDate ?? data.createdAt
    const endDisplay = data.endDate ? formatDate(data.endDate, { dateStyle: 'long', timeStyle: 'short' }) : 'Kapan saja'
    dateDisplay = `${formatDate(displayStart, { dateStyle: 'long', timeStyle: 'short' })} — ${endDisplay}`
  }

  return (
    <section className={cn(baseClass, 'srd:relative srd:overflow-hidden')}>
      <div className='srd:w-full srd:max-w-[1440px] srd:mx-auto srd:flex srd:flex-col srd:gap-y-4 srd:px-4'>
        <div className='srd:flex srd:flex-col'>
          <span className={cn(typographyVariants({ variant: 'title-md' }))}>Papan Peringkat</span>

          <h1 className={cn(typographyVariants({ variant: 'headline-md' }))}>{data.title}</h1>

          {dateDisplay && (
            <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-grey-100')}>{dateDisplay}</span>
          )}
        </div>

        {data.type === 'course' && data.host && (
          <div className='srd:flex srd:flex-col srd:gap-y-2'>
            <span className={cn(typographyVariants({ variant: 'body-sm' }))}>Guru pendamping:</span>

            <div className='srd:flex srd:items-center srd:gap-2 srd:flex-wrap'>
              <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
                <AvatarImage src={data.host.image ?? undefined} alt={data.host.name} />
                <AvatarFallback>{getInitials(data.host.name)}</AvatarFallback>
              </Avatar>
              <span className={cn(typographyVariants({ variant: 'body-lg' }))}>{data.host.name}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
